// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AUTH0 } from '../utils/config';
import { setTokenProvider, setOnUnauthorized } from '../utils/unifiedApi';
import { forgetPushDeviceOnLogout } from '../utils/pushNotifications';

const AuthContext = createContext();

// Helper function to decode JWT token
const decodeJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

// Helper function to get subdomain from current URL
const getSubdomain = () => {
  const hostname = window.location.hostname;
  
  // Handle localhost/development scenarios
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'basoccer'; // Default tenant for local development
  }
  
  const parts = hostname.split('.');
  // For subdomains like basoccer.quailgame.com
  if (parts.length >= 3) {
    return parts[0];
  }
  
  return null;
};

export const AuthProvider = ({ children }) => {
  const {
    user: auth0User,
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0IsLoading,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();
  
  const [user, setUser] = useState(null);
  const [subdomain, setSubdomain] = useState(null);
  const [backendValidated, setBackendValidated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // Set subdomain on mount (from URL, not JWT)
  useEffect(() => {
    const currentSubdomain = getSubdomain();
    setSubdomain(currentSubdomain);
  }, []);

  // Enhanced user object with role information and backend validation
  useEffect(() => {
    const extractUserInfo = async () => {
      if (auth0IsAuthenticated && auth0User && !auth0IsLoading) {
        let userPermissions = [];
        
        try {
          // Get access token and decode it to extract permissions
          const token = await getAccessTokenSilently();
          console.log('Auth0 token obtained successfully');
          
          // Decode JWT to extract permissions (tenant_id no longer in JWT)
          const decodedToken = decodeJWT(token);
          
          if (decodedToken) {
            console.log('JWT decoded successfully for user:', auth0User.email);
            // Extract permissions from the decoded token
            // Permissions can be a single value or an array
            const permissions = decodedToken.permissions;
            if (permissions) {
              userPermissions = Array.isArray(permissions) ? permissions : [permissions];
            }
          } else {
            console.warn('JWT decode returned null, but continuing with auth');
          }
          
          // Set backend validated even if token decode returns null
          // The user is authenticated via Auth0, that's sufficient
          setBackendValidated(true);
          
          // Extract permissions from Auth0 user or JWT token
          const enhancedUser = {
            ...auth0User,
            role: decodedToken?.['https://api.quailgame.com/roles']?.[0] || 'player', // Custom claim from JWT
            name: auth0User.name || auth0User.nickname || auth0User.email,
            permissions: userPermissions, // Permissions from JWT
            canAccessAdmin: decodedToken?.['https://api.quailgame.com/can_access_admin'] || false, // Admin access claim
            roles: decodedToken?.['https://api.quailgame.com/roles'] || [], // Roles array from JWT
          };
          setUser(enhancedUser);
          setAuthChecked(true);
        } catch (error) {
          console.error('Error processing authentication token:', error);
          // If getting token fails, user is not properly authenticated
          // Check if it's a login_required error (user needs to log in again)
          if (error.error === 'login_required' || error.message?.includes('login_required')) {
            console.log('Session expired or invalid, user needs to log in again');
            setUser(null);
            setBackendValidated(false);
          } else {
            // For other errors (network issues, etc.), still allow authenticated access
            // if Auth0 says user is authenticated - let backend handle authorization
            setBackendValidated(true);
          }
          setAuthChecked(true);
        }
      } else if (!auth0IsAuthenticated && !auth0IsLoading) {
        // Clear state when user is not authenticated
        setUser(null);
        setBackendValidated(false);
        setAuthChecked(true);
      }
      // Note: Don't set authChecked when auth0IsLoading is true
      // We need to wait for Auth0 to finish loading first
    };

    // Only run if Auth0 is done loading
    if (!auth0IsLoading) {
      extractUserInfo();
    }
  }, [auth0IsAuthenticated, auth0User, auth0IsLoading, getAccessTokenSilently]);

  // Register token provider for unifiedApi
  useEffect(() => {
    setTokenProvider(async () => {
      try {
        return await getAccessTokenSilently();
      } catch {
        return null;
      }
    });
  }, [getAccessTokenSilently]);

  // Register 401 handler - clear stale auth state when backend rejects the token
  useEffect(() => {
    setOnUnauthorized(() => {
      console.log('Received 401 from API - clearing stale auth state');
      setUser(null);
      setBackendValidated(false);
      setAuthChecked(true);
    });
  }, []);

  const login = (redirectPath = null) => {
    // Always use the configured redirect URI from environment
    // The redirectPath will be handled in the CallbackPage after auth.
    // Only in-app paths count (e.g. a click event passed by onClick={login} must not be stored).
    if (typeof redirectPath === 'string' && redirectPath.startsWith('/') && !redirectPath.startsWith('//')) {
      sessionStorage.setItem('postLoginRedirect', redirectPath);
    } else {
      sessionStorage.removeItem('postLoginRedirect');
    }
    
    loginWithRedirect({
      authorizationParams: {
        redirect_uri: AUTH0.redirectUri
      }
    });
  };

  const logoutUser = async () => {
    // Stop chat notifications for this member on a possibly shared device before signing out.
    await forgetPushDeviceOnLogout();
    logout({
      logoutParams: {
        returnTo: AUTH0.logoutUri
      }
    });
  };

  // Helper function to check if user has a specific permission
  const hasPermission = useCallback((permission) => {
    // Check for admin access using the JWT claim
    if (permission === 'admin:tenant') {
      return user?.canAccessAdmin || false;
    }
    
    // For other permissions, check the permissions array
    return user?.permissions?.includes(permission) || false;
  }, [user?.permissions, user?.canAccessAdmin]);

  const value = {
    user,
    isAuthenticated: auth0IsAuthenticated && backendValidated, // Both must be true
    isLoading: auth0IsLoading || !authChecked, // Loading until both Auth0 and backend are checked
    subdomain, // Subdomain from URL (replaces tenantId from JWT)
    login,
    logout: logoutUser,
    getAccessTokenSilently,
    hasPermission, // Add permission checker
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
